import { useEffect } from 'react'
import { detect } from 'detect-browser'
import { create } from 'zustand'
import { mobileOSList, tabletUserAgent } from './constants'
import { shallow } from 'zustand/shallow'
import { isProductionLikeEnvironment } from '../../utils/runtime-environment'

const initialState = {
  isProdOrStaging: isProductionLikeEnvironment,
  windowWidth: window.innerWidth,
  windowHeight: window.innerHeight,
  menuOpen: window.innerWidth > 1024,
  // how many mounted screens hide the mobile bottom bar (see useHideBottomBar)
  bottomBarHiders: 0,
  popupOverlay: false,
  mobilePopupOverlay: false,
  browserInfo: detect()
}

export const useBrowserStore = create((set, get) => ({
  ...initialState,

  setWindowSize: (width, height) =>
    set({ windowWidth: width, windowHeight: height }),

  setMenuOpen: (isOpen) => set({ menuOpen: isOpen }),

  setShowMobilePopupOverlay: (isOpen) => set({ mobilePopupOverlay: isOpen }),

  toggleMenu: () => set((state) => ({ menuOpen: !state.menuOpen })),
  closeMenu: () => set({ menuOpen: false }),

  logout: () => set(initialState),

  popupOverlayActions: {
    show: () => set({ popupOverlay: true }),
    hide: () => set({ popupOverlay: false })
  },

  bottomBarActions: {
    hide: () =>
      set((state) => ({ bottomBarHiders: state.bottomBarHiders + 1 })),
    release: () =>
      set((state) => ({
        bottomBarHiders: Math.max(0, state.bottomBarHiders - 1)
      }))
  }
}))

export const useIsBottomBarVisible = () =>
  useBrowserStore((state) => state.bottomBarHiders === 0)

// Hides the mobile bottom bar while the calling component is mounted. A count
// instead of a boolean, so the order in which screens mount and unmount (a new
// plan mounting before the old one unmounts, hot reload) can't leave it shown.
export const useHideBottomBar = () => {
  const { hide, release } = useBrowserStore((state) => state.bottomBarActions)

  useEffect(() => {
    hide()

    return release
  }, [hide, release])
}

// selectors
export const useShowPopupOverlay = () =>
  useBrowserStore((state) => state.popupOverlayActions.show, shallow)

export const useHidePopupOverlay = () =>
  useBrowserStore((state) => state.popupOverlayActions.hide, shallow)

export const useWindowWidth = () =>
  useBrowserStore((state) => state.windowWidth, shallow)

export const useWindowHeight = () =>
  useBrowserStore((state) => state.windowHeight, shallow)

export const useBrowserInfo = () =>
  useBrowserStore((state) => state.browserInfo, shallow)

export const useIsMobile = () => {
  const windowWidth = useWindowWidth()
  const browserInfo = useBrowserInfo()

  const isMobileOS = mobileOSList.includes(browserInfo?.os)

  const isTablet = tabletUserAgent.some((uA) =>
    window.navigator.userAgent.includes(uA)
  )
  return windowWidth < 900 && isMobileOS && !isTablet
}

export const useIsTablet = () => {
  const windowWidth = useWindowWidth()
  const browserInfo = useBrowserInfo()

  const isMobileOS = mobileOSList.includes(browserInfo?.os)
  return windowWidth >= 900 && isMobileOS
}

export const useIsDesktop = () => {
  const windowWidth = useWindowWidth()

  return windowWidth > 1024
}

// Static
const getWindowWidth = () => useBrowserStore.getState().windowWidth ?? null
const getBrowserInfo = () => useBrowserStore.getState().browserInfo ?? null

export const getIsMobile = () => {
  const windowWidth = getWindowWidth()
  const browserInfo = getBrowserInfo()

  const isMobileOS = mobileOSList.includes(browserInfo?.os)

  const isTablet = tabletUserAgent.some((uA) =>
    window.navigator.userAgent.includes(uA)
  )
  return windowWidth < 900 && isMobileOS && !isTablet
}

export const getIsTablet = () => {
  const windowWidth = getWindowWidth()
  const browserInfo = getBrowserInfo()

  const isMobileOS = mobileOSList.includes(browserInfo?.os)
  return windowWidth >= 900 && isMobileOS
}

export const getIsDesktop = () => {
  const windowWidth = getWindowWidth()
  const browserInfo = getBrowserInfo()

  const isMobileOS = mobileOSList.includes(browserInfo?.os)
  return windowWidth >= 900 && isMobileOS
}
